import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Table } from '../components/ui/Table.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { formatDateTime } from '../utils/formatters.js';

import { TenantDeleteModal } from './TenantDeleteModal.jsx';

import { usePlatformAuth } from './PlatformAuthContext.jsx';
import { platformAuditApi, tenantsApi } from './platformApi.js';
import './platform.css';

const STATUS_VARIANT = {
  active: 'success',
  provisioning: 'warning',
  blocked: 'danger',
  failed: 'danger',
  deleting: 'warning',
  deleted: 'neutral',
};

/* Nur diese Felder sind pflegbar. Das Kürzel fehlt bewusst: daran hängen
   später die Datenbanknamen und die Postgres-Rolle der Firma. */
const TEXT_FIELDS = ['name', 'contactName', 'contactEmail', 'contactPhone', 'plan', 'notes'];
const DATE_FIELDS = ['contractStart', 'contractEnd'];

function toForm(tenant) {
  const out = {};
  for (const key of TEXT_FIELDS) {
    out[key] = tenant?.[key] == null ? '' : String(tenant[key]);
  }
  for (const key of DATE_FIELDS) {
    // Datumsfelder kommen als Zeitstempel zurück, das Eingabefeld will YYYY-MM-DD.
    out[key] = tenant?.[key] == null ? '' : String(tenant[key]).slice(0, 10);
  }
  return out;
}

export function TenantDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions } = usePlatformAuth();
  const canWrite = permissions?.tenants === 'write';
  const canSeeAudit = permissions?.platformAudit === 'read';

  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const tenant = useQuery({
    queryKey: ['platform', 'tenant', id],
    queryFn: () => tenantsApi.get(id),
  });

  const audit = useQuery({
    queryKey: ['platform', 'audit', id],
    queryFn: () => platformAuditApi.list({ tenantId: id, limit: 50 }),
    enabled: canSeeAudit,
  });

  /* Formular erst füllen, wenn die Akte da ist, und danach nicht mehr
     überschreiben - sonst verliert man beim Tippen jede Eingabe, sobald die
     Abfrage im Hintergrund neu läuft. */
  useEffect(() => {
    if (tenant.data && form === null) {
      setForm(toForm(tenant.data));
    }
  }, [tenant.data, form]);

  const save = useMutation({
    mutationFn: (patch) => tenantsApi.update(id, patch),
    onSuccess: (updated) => {
      setError(null);
      setSaved(true);
      queryClient.setQueryData(['platform', 'tenant', id], updated);
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['platform', 'audit', id] });
    },
    onError: (err) => {
      setSaved(false);
      setError(err?.message || t('platform.tenants.save.failed'));
    },
  });

  const [deleteOpen, setDeleteOpen] = useState(false);

  const setStatus = useMutation({
    /* Eigener Endpunkt statt PATCH status: das Sperren beendet zugleich JEDE
       laufende Sitzung der Firma. Über die allgemeine Änderung ginge das
       unter, und jemand mit gültigem Zugriffstoken könnte bis zu einer
       Viertelstunde weiterarbeiten. */
    mutationFn: (status) => tenantsApi.setStatus(id, status),
    onSuccess: (updated) => {
      queryClient.setQueryData(['platform', 'tenant', id], updated);
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['platform', 'audit', id] });
    },
  });

  if (tenant.isLoading || form === null) {
    return <p className="muted">{t('platform.tenants.loading')}</p>;
  }
  if (tenant.error) {
    return <div className="alert danger">{tenant.error.message || t('common.loadError')}</div>;
  }

  const row = tenant.data;
  const blocked = row.status === 'blocked';
  const canToggle = canWrite && row.status !== 'provisioning' && row.status !== 'failed';

  function field(key, label, type = 'text') {
    return (
      <label className="field" key={key}>
        <span className="field-label">{label}</span>
        <input
          className="input"
          type={type}
          value={form[key]}
          disabled={!canWrite}
          onChange={(e) => {
            setSaved(false);
            setForm((f) => ({ ...f, [key]: e.target.value }));
          }}
        />
      </label>
    );
  }

  const auditColumns = [
    { key: 'occurredAt', label: t('platform.audit.col.when'), render: (r) => formatDateTime(r.occurredAt) },
    { key: 'actorEmail', label: t('platform.audit.col.who'), render: (r) => r.actorEmail || '-' },
    { key: 'action', label: t('platform.audit.col.what'), render: (r) => t(`platform.audit.action.${r.action}`) },
    { key: 'entity', label: t('platform.audit.col.entity'), render: (r) => r.entity || '-' },
  ];

  return (
    <>
      <div className="pf-page-head">
        <div>
          <p className="muted text-xs" style={{ margin: '0 0 var(--space-1)' }}>
            <Link to="/platform/tenants">{t('platform.nav.tenants')}</Link>
          </p>
          <h1>{row.name}</h1>
          <p>
            <span className="mono">{row.slug}</span>
            <span style={{ marginLeft: 'var(--space-3)' }}>
              <Badge variant={STATUS_VARIANT[row.status] || 'neutral'}>
                {t(`platform.tenants.status.${row.status}`)}
              </Badge>
            </span>
          </p>
        </div>
        <div className="pf-badges">
          {/* Die Benutzerverwaltung der Firma. Ein "als Kunde anmelden" gibt
              es dort bewusst nicht - es existiert kein Codepfad dafür. */}
          <Link className="btn ghost" to={`/platform/tenants/${id}/users`}>
            {t('platform.users.title')}
          </Link>
          {canToggle && (
          <Button
            variant={blocked ? 'primary' : 'danger'}
            disabled={setStatus.isPending}
            onClick={() => setStatus.mutate(blocked ? 'active' : 'blocked')}
          >
            {blocked ? t('platform.tenants.unblock') : t('platform.tenants.block')}
          </Button>
          )}
          {/* Entfernen erscheint erst, wenn die Firma gesperrt ist. Der Server
              setzt dieselbe Bedingung noch einmal durch - aus einem Klick wird
              so ein Vorgang mit einer Denkpause dazwischen. */}
          {canWrite && blocked && (
            <Button variant="danger" onClick={() => setDeleteOpen(true)}>
              {t('platform.tenants.delete.cta')}
            </Button>
          )}
        </div>
      </div>

      {row.status === 'provisioning' && <div className="pf-note">{t('platform.tenants.provisioningNote')}</div>}
      {blocked && <div className="pf-note">{t('platform.tenants.blockedNote')}</div>}

      <div className="card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
        <h2 className="card-title">{t('platform.tenants.masterData')}</h2>

        {field('name', t('platform.tenants.col.name'))}
        {field('contactName', t('platform.tenants.col.contact'))}
        {field('contactEmail', t('common.email'), 'email')}
        {field('contactPhone', t('platform.tenants.col.phone'))}
        {field('plan', t('platform.tenants.col.plan'))}
        {field('contractStart', t('platform.tenants.col.contractStart'), 'date')}
        {field('contractEnd', t('platform.tenants.col.contractEnd'), 'date')}

        <label className="field">
          <span className="field-label">{t('platform.tenants.col.notes')}</span>
          <textarea
            className="input"
            rows={3}
            value={form.notes}
            disabled={!canWrite}
            onChange={(e) => {
              setSaved(false);
              setForm((f) => ({ ...f, notes: e.target.value }));
            }}
          />
        </label>

        {error && <div className="alert danger">{error}</div>}
        {saved && <div className="alert success">{t('platform.tenants.saved')}</div>}

        {canWrite && (
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
            <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate(form)}>
              {save.isPending ? t('platform.tenants.create.saving') : t('common.save')}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/platform/tenants')}>
              {t('common.back')}
            </Button>
          </div>
        )}
      </div>

      {canSeeAudit && (
        <>
          <h2 className="card-title">{t('platform.audit.title')}</h2>
          <p className="muted text-sm" style={{ marginTop: 0 }}>{t('platform.audit.subtitle')}</p>
          <Table
            columns={auditColumns}
            rows={audit.data?.items || []}
            isLoading={audit.isLoading}
            emptyMessage={t('platform.audit.empty')}
          />
        </>
      )}

      <TenantDeleteModal open={deleteOpen} tenant={row} onClose={() => setDeleteOpen(false)} />
    </>
  );
}
